import React from 'react';
import { motion } from 'framer-motion';
import { Droplets } from 'lucide-react';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'spinner' | 'dots' | 'pulse' | 'water';
  text?: string;
  full_screen?: boolean;
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  variant = 'spinner',
  text,
  full_screen = false,
  className = ''
}) => {
  const size_classes = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };
  
  const text_size_classes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };
  
  const icon_sizes = {
    sm: 24,
    md: 32,
    lg: 48,
    xl: 64
  };
  
  const render_spinner = () => {
    switch (variant) {
      case 'spinner':
        return (
          <div
            className={`${size_classes[size]} border-4 border-gray-200 border-t-forest-green rounded-full animate-spin`}
          />
        );
      
      case 'dots':
        return (
          <div className="flex gap-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className={`${size === 'sm' ? 'w-2 h-2' : size === 'md' ? 'w-3 h-3' : size === 'lg' ? 'w-4 h-4' : 'w-5 h-5'} bg-forest-green rounded-full`}
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [1, 0.5, 1]
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: i * 0.2
                }}
              />
            ))}
          </div>
        );
      
      case 'pulse':
        return (
          <motion.div
            className={`${size_classes[size]} bg-forest-green rounded-full`}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [1, 0.7, 1]
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />
        );
      
      case 'water':
        const icon_size = icon_sizes[size];
        return (
          <div className="relative" style={{ width: icon_size, height: icon_size }}>
            {/* Droplet outline */}
            <Droplets
              className="text-gray-300 absolute inset-0"
              size={icon_size}
              strokeWidth={2}
            />
            
            {/* Water fill with wave animation - using clip-path to stay inside droplet */}
            <div className="absolute inset-0" style={{ clipPath: 'url(#droplet-clip)' }}>
              <motion.div
                className="absolute inset-0 w-full"
                initial={{ y: '100%' }}
                animate={{ y: '0%' }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  repeatDelay: 0.2
                }}
              >
                {/* Main water body with gradient */}
                <div 
                  className="absolute inset-0 w-full h-full"
                  style={{
                    background: 'linear-gradient(180deg, rgba(59, 130, 246, 0.7) 0%, rgba(37, 99, 235, 0.85) 50%, rgba(29, 78, 216, 0.95) 100%)'
                  }}
                />
                
                {/* Animated wave overlay */}
                <svg
                  className="absolute top-0 left-0 w-full"
                  style={{ height: '20%', transform: 'translateY(-50%)' }}
                  viewBox="0 0 100 20"
                  preserveAspectRatio="none"
                >
                  <motion.path
                    d="M0,10 Q12.5,5 25,10 T50,10 T75,10 T100,10 L100,20 L0,20 Z"
                    fill="rgba(147, 197, 253, 0.6)"
                    animate={{
                      d: [
                        "M0,10 Q12.5,5 25,10 T50,10 T75,10 T100,10 L100,20 L0,20 Z",
                        "M0,10 Q12.5,15 25,10 T50,10 T75,10 T100,10 L100,20 L0,20 Z",
                        "M0,10 Q12.5,5 25,10 T50,10 T75,10 T100,10 L100,20 L0,20 Z"
                      ]
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'easeInOut'
                    }}
                  />
                </svg>
                
                {/* Light shimmer effect */}
                <motion.div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
                    width: '150%',
                    left: '-50%'
                  }}
                  animate={{
                    x: ['0%', '100%']
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'linear'
                  }}
                />
              </motion.div>
            </div>
            
            {/* SVG clip path definition matching droplet shape - adjusted for double droplet */}
            <svg width="0" height="0">
              <defs>
                <clipPath id="droplet-clip" clipPathUnits="objectBoundingBox">
                  {/* Main large droplet */}
                  <path d="M0.5,0.05 C0.3,0.25 0.15,0.4 0.15,0.6 C0.15,0.8 0.3,0.95 0.5,0.95 C0.7,0.95 0.85,0.8 0.85,0.6 C0.85,0.4 0.7,0.25 0.5,0.05 Z" />
                  {/* Small droplet on the right */}
                  <path d="M0.72,0.28 C0.65,0.35 0.6,0.4 0.6,0.48 C0.6,0.56 0.65,0.61 0.72,0.61 C0.79,0.61 0.84,0.56 0.84,0.48 C0.84,0.4 0.79,0.35 0.72,0.28 Z" />
                </clipPath>
              </defs>
            </svg>
          </div>
        );
      
      default:
        return null;
    }
  };
  
  const content = (
    <div className={`flex flex-col items-center justify-center gap-4 ${className}`}>
      {render_spinner()}
      {text && (
        <p className={`${text_size_classes[size]} text-gray-600 font-medium animate-pulse`}>
          {text}
        </p>
      )}
    </div>
  );
  
  if (full_screen) {
    return (
      <div className="fixed inset-0 bg-white bg-opacity-90 backdrop-blur-sm flex items-center justify-center z-50">
        {content}
      </div>
    );
  }
  
  return content;
};

// Skeleton Loader Component
interface SkeletonProps {
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'text',
  width,
  height,
  className = ''
}) => {
  const get_variant_classes = () => {
    switch (variant) {
      case 'circular':
        return 'rounded-full';
      case 'rectangular':
        return 'rounded-md';
      case 'text':
      default:
        return 'rounded h-4';
    }
  };
  
  const style = {
    width: width || (variant === 'circular' ? '40px' : '100%'),
    height: height || (variant === 'circular' ? '40px' : undefined)
  };
  
  return (
    <div
      className={`bg-gray-200 animate-pulse ${get_variant_classes()} ${className}`}
      style={style}
    />
  );
};

// Skeleton Group for common layouts
export const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
    <div className="flex items-center gap-3">
      <Skeleton variant="circular" width="48px" height="48px" />
      <div className="flex-1 space-y-2">
        <Skeleton width="60%" />
        <Skeleton width="40%" />
      </div>
    </div>
    <div className="space-y-2">
      <Skeleton />
      <Skeleton width="90%" />
      <Skeleton width="70%" />
    </div>
  </div>
);

export const SkeletonTable: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
    <div className="bg-gray-50 p-4 border-b border-gray-200">
      <div className="flex gap-4">
        <Skeleton width="25%" />
        <Skeleton width="25%" />
        <Skeleton width="25%" />
        <Skeleton width="25%" />
      </div>
    </div>
    <div className="divide-y divide-gray-200">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="p-4">
          <div className="flex gap-4">
            <Skeleton width="25%" />
            <Skeleton width="25%" />
            <Skeleton width="25%" />
            <Skeleton width="25%" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Page Loading Component
interface PageLoadingProps {
  message?: string;
}

export const PageLoading: React.FC<PageLoadingProps> = ({
  message = 'Loading...'
}) => (
  <div className="min-h-screen flex items-center justify-center bg-off-white">
    <div className="text-center">
      <LoadingSpinner size="xl" variant="water" text={message} />
    </div>
  </div>
);
